import { ExternalRedirect } from "@/components/ExternalRedirect";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { NewsletterPopup } from "@/components/NewsletterPopup";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { MiniCartProvider } from "@/contexts/MiniCartContext";
import { SetUpgradeModal } from "@/components/cart/SetUpgradeModal";
import { UrlPromoCode } from "@/components/cart/UrlPromoCode";
import { MiniCart } from "@/components/cart/MiniCart";
import { UrlCanonicalizer } from "./components/UrlCanonicalizer";
import { AffiliateCapture } from "@/components/affiliate/AffiliateCapture";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";

// Every top-level route is code-split so a visitor only downloads the page
// they actually opened. Shared shell pieces (Header, Footer, newsletter,
// design system) stay in the main bundle on purpose.
const Index = lazy(() => import("./pages/Index"));
const OrderOverview = lazy(() => import("./pages/OrderOverview"));
const CheckoutReturn = lazy(() => import("./pages/CheckoutReturn"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));
const AGB = lazy(() => import("./pages/AGB"));
const Widerruf = lazy(() => import("./pages/Widerruf"));
const Garantie = lazy(() => import("./pages/Garantie"));
const RoutineGuide = lazy(() => import("./pages/RoutineGuide"));
const Versand = lazy(() => import("./pages/Versand"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const EmailUnsubscribe = lazy(() => import("./pages/EmailUnsubscribe"));
const NewsletterConfirmed = lazy(() => import("./pages/NewsletterConfirmed"));
const NewsletterFailed = lazy(() => import("./pages/NewsletterFailed"));
const Glp1Begleitung = lazy(() => import("./pages/seo/Glp1Begleitung"));
const AbnehmspritzeBegleitung = lazy(() => import("./pages/seo/AbnehmspritzeBegleitung"));
const ErnaehrungBeiGlp1 = lazy(() => import("./pages/seo/ErnaehrungBeiGlp1"));
const SupplementeBeiGlp1 = lazy(() => import("./pages/seo/SupplementeBeiGlp1"));
const Glp1Nebenwirkungen = lazy(() => import("./pages/seo/Glp1Nebenwirkungen"));
const Abnehmspritze = lazy(() => import("./pages/seo/Abnehmspritze"));
const Glp1Abnehmen = lazy(() => import("./pages/seo/Glp1Abnehmen"));
const AbnehmspritzeNebenwirkungen = lazy(() => import("./pages/seo/AbnehmspritzeNebenwirkungen"));
const Glp1AnbieterVergleich = lazy(() => import("./pages/seo/Glp1AnbieterVergleich"));
const H2humansProdukte = lazy(() => import("./pages/seo/H2humansProdukte"));
const Glp1Phasen = lazy(() => import("./pages/seo/Glp1Phasen"));
const BeActive = lazy(() => import("./pages/BeActive"));
const Shop = lazy(() => import("./pages/Shop"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const BlogIndex = lazy(() => import("./pages/blog/BlogIndex"));
const CategoryOrAffiliate = lazy(() => import("./components/affiliate/CategoryOrAffiliate").then((m) => ({ default: m.CategoryOrAffiliate })));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const BlogPostPage = lazy(() => import("./pages/blog/BlogPostPage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminBlogList = lazy(() => import("./pages/admin/AdminBlogList"));
const AdminBlogEditor = lazy(() => import("./pages/admin/AdminBlogEditor"));
const AdminBlogAnalytics = lazy(() => import("./pages/admin/AdminBlogAnalytics"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const ReviewSubmit = lazy(() => import("./pages/ReviewSubmit"));
const AdminGuard = lazy(() =>
  import("./components/admin/AdminGuard").then((m) => ({ default: m.AdminGuard })),
);

const queryClient = new QueryClient();

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * Reserves the full viewport height while a route chunk loads so the page
 * never shifts once the real content mounts (keeps CLS at 0).
 */
const RouteFallback = () => (
  <div className="min-h-screen w-full bg-background" aria-hidden="true" />
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <CartProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <MiniCartProvider>
            <UrlCanonicalizer />
            <MiniCart />
            <SetUpgradeModal />
            <AffiliateCapture />
            <UrlPromoCode />
            <ScrollToTop />
            <CookieConsentBanner />
            <NewsletterPopup />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/be-active" element={<BeActive />} />
                <Route path="/bestellung" element={<OrderOverview />} />
                <Route path="/checkout/return" element={<CheckoutReturn />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/agb" element={<AGB />} />
                <Route path="/widerruf" element={<Widerruf />} />
                <Route path="/garantie" element={<Garantie />} />
                <Route path="/routine-guide" element={<RoutineGuide />} />
                <Route path="/versand" element={<Versand />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/ueber-uns" element={<About />} />
                <Route path="/about" element={<Navigate to="/ueber-uns" replace />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />
                <Route path="/newsletter/bestaetigt" element={<NewsletterConfirmed />} />
                <Route path="/newsletter/fehler" element={<NewsletterFailed />} />
                <Route path="/glp-1-begleitung" element={<Glp1Begleitung />} />
                <Route path="/abnehmspritze-begleitung" element={<AbnehmspritzeBegleitung />} />
                <Route path="/ernaehrung-bei-glp-1" element={<ErnaehrungBeiGlp1 />} />
                <Route path="/supplemente-bei-glp-1" element={<SupplementeBeiGlp1 />} />
                <Route path="/glp-1-nebenwirkungen" element={<Glp1Nebenwirkungen />} />
                <Route path="/abnehmspritze" element={<Abnehmspritze />} />
                <Route path="/glp-1-abnehmen" element={<Glp1Abnehmen />} />
                <Route path="/abnehmspritze-nebenwirkungen" element={<AbnehmspritzeNebenwirkungen />} />
                <Route path="/glp-1-anbieter-vergleich" element={<Glp1AnbieterVergleich />} />
                <Route path="/h2humans-produkte" element={<H2humansProdukte />} />
                <Route path="/glp-1-phasen" element={<Glp1Phasen />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/bewertung" element={<ReviewSubmit />} />
                <Route path="/admin/reviews" element={<AdminGuard><AdminReviews /></AdminGuard>} />
                <Route path="/produkte/:slug" element={<ProductPage />} />
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/admin" element={<Navigate to="/admin/blog" replace />} />
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/blog" element={<AdminGuard><AdminBlogList /></AdminGuard>} />
<Route path="/admin/blog/analytics" element={<AdminGuard><AdminBlogAnalytics /></AdminGuard>} />
                <Route path="/admin/blog/:postId" element={<AdminGuard><AdminBlogEditor /></AdminGuard>} />
                <Route path="/admin/affiliates" element={<AdminAffiliates />} />
                <Route path="/pilot" element={<ExternalRedirect to="https://h2h-aion.lovable.app/aion" />} />
                <Route path="/aion" element={<ExternalRedirect to="https://h2h-aion.lovable.app/aion" />} />
                <Route path="/:categorySlug" element={<CategoryOrAffiliate />} />
                <Route path="/:categorySlug/:postSlug" element={<BlogPostPage />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </MiniCartProvider>
          </BrowserRouter>
        </TooltipProvider>
      </CartProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
